import { __, declension } from '../../utils';
import type { IFeedItem } from '../FeedItem/FeedItem';
import Feed from './Feed';
import FeedSegment, { type FeedSegmentValue } from './FeedSegment';
import FeedStats from './FeedStats';
import {
  Box,
  Button,
  FilterChip,
  Skeleton,
  Stack,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  items: IFeedItem[];
  /**
   * Zoznam pre čísla v štatistike - to isté okolie, len bez filtrov stavu.
   * Viď [[FeedStats]]: číslo musí povedať, koľko čoho je, nie koľko z toho
   * práve prešlo mojím vlastným filtrom.
   */
  countItems: IFeedItem[];
  isLoading: boolean;
  segment: FeedSegmentValue;
  onSegmentChange: (value: FeedSegmentValue) => void;
  statFilters: React.ComponentProps<typeof FeedStats>['value'];
  onStatToggle: React.ComponentProps<typeof FeedStats>['onToggle'];
  /** text tlačidla zoradenia, napr. „od najnovších" */
  sortLabel?: string;
  onSortClick?: () => void;
  /** vybraná oblasť na mape; `null` = zoznam nie je zúžený na klaster */
  cluster?: { count: number } | null;
  onClearCluster?: () => void;
  onItemMapClick?: (itemId: string) => void;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  searchLocation?: string;
  hasNoNewsPreferences?: boolean;
  /** hustejšie rozloženie pre mobilný sheet */
  compact?: boolean;
  /** skryje riadky filtrov a zoradenia - zatvorený mobilný sheet */
  collapsed?: boolean;
  /** pamäť pozície zoznamu po segmentoch, viď `Feed` */
  scrollStore?: React.ComponentProps<typeof Feed>['scrollStore'];
};

/**
 * Obsah panela na hlavnej stránke. Poradie riadkov je zo zadania záväzné:
 * segment, štatistika, zoradenie, zoznam - a pás vybranej oblasti medzi
 * zoradením a zoznamom, keď je nejaká vybraná.
 *
 * Panel je ten istý na desktope aj v mobilnom sheete; líšia sa len obalom
 * (plávajúca karta vs. vysúvací list) a `compact` / `collapsed`.
 */
const FeedPanel: React.FC<Props> = ({
  items,
  countItems,
  isLoading,
  segment,
  onSegmentChange,
  statFilters,
  onStatToggle,
  sortLabel,
  onSortClick,
  cluster = null,
  onClearCluster,
  onItemMapClick,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  searchLocation,
  hasNoNewsPreferences = false,
  compact = false,
  collapsed = false,
  scrollStore,
}) => {
  const reports = segment === 'reports';

  return (
    <Stack sx={{ height: 1, minHeight: 0, gap: compact ? 1.25 : 1.75 }}>
      <FeedSegment value={segment} onChange={onSegmentChange} />

      {/* Novinky stavy nemajú, takže štatistika ani zoradenie podľa hlasov
          preň nedávajú zmysel - v tom segmente je pod prepínačom rovno zoznam. */}
      {reports && !collapsed ? (
        <>
          <FeedStats
            countItems={countItems}
            value={statFilters}
            onToggle={onStatToggle}
            compact={compact}
            loading={isLoading}
          />

          <Stack
            direction='row'
            sx={{ flex: 'none', alignItems: 'center', gap: 1, px: 0.25 }}
          >
            {/* Kým sa okolie načítava, nekreslí sa ani tu nula - je to to isté
                číslo ako v bunkách nad tým, len zhrnuté do vety. */}
            {isLoading ? (
              <Skeleton variant='rounded' width={64} height={16} />
            ) : (
              <Box
                component='span'
                sx={(theme) => ({
                  fontSize: theme.typography.pxToRem(13),
                  color: theme.palette.grey[500],
                })}
              >
                {items.length} {__('v okolí')}
              </Box>
            )}
            {onSortClick ? (
              <Box sx={{ marginLeft: 'auto' }}>
                <Button variant='text' size='small' onClick={onSortClick}>
                  {sortLabel || __('Zoradiť')}
                </Button>
              </Box>
            ) : null}
          </Stack>
        </>
      ) : null}

      {/* Vybraná oblasť je aktívny filter zoznamu, takže je to `FilterChip`
          z knižnice - ten istý tvar, aký má aktívny filter nad tabuľkou
          v administrácii. Ručne skladaný žltý blok, ktorý tu stál predtým, bol
          druhá podoba tej istej veci. */}
      {cluster && !collapsed ? (
        <Box sx={{ flex: 'none' }}>
          <FilterChip
            label={__('Oblasť')}
            value={`${cluster.count} ${declension(cluster.count, [
              __('podnet'),
              __('podnety'),
              __('podnetov'),
            ])}`}
            onRemove={onClearCluster}
          />
        </Box>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <Feed
          // Každý segment je vlastný zoznam s vlastnou pozíciou - prepnutie
          // ho postaví nanovo a ten si obnoví tú svoju.
          key={segment}
          scrollStore={scrollStore}
          scrollKey={segment}
          plain
          items={items}
          isLoading={isLoading}
          onItemMapClick={onItemMapClick}
          hasNoNewsPreferences={hasNoNewsPreferences}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
          searchLocation={searchLocation}
        />
      </Box>
    </Stack>
  );
};

export default FeedPanel;
